<template>
  <div class="hl">
    <div class="hl-head">
      <div>
        <p class="hl-eyebrow">Celebraciones</p>
        <h2 class="hl-title">Horarios de culto</h2>
      </div>

      <!-- Pestañas cuando hay varios bloques (p. ej. invierno / verano) -->
      <div v-if="bloquesOrdenados.length > 1" class="hl-tabs" role="tablist" aria-label="Temporada">
        <button
          v-for="bloque in bloquesOrdenados"
          :key="bloque.id"
          type="button"
          role="tab"
          class="hl-tab"
          :class="{ 'is-active': bloque.id === bloqueActivo.id }"
          :aria-selected="bloque.id === bloqueActivo.id"
          v-track:cambiar_horario="{ temporada: bloque.titulo }"
          @click="seleccionado = bloque.id"
        >
          {{ bloque.titulo }}
          <span v-if="bloque.activo" class="hl-tab-dot" title="Horario vigente"></span>
        </button>
      </div>
    </div>

    <!-- Estados -->
    <div v-if="loading" class="hl-grid">
      <div v-for="n in 3" :key="n" class="hl-skeleton" :class="{ 'is-featured': n === 1 }"></div>
    </div>

    <p v-else-if="error || !bloqueActivo" class="hl-empty">
      {{ error ? "No se pudieron cargar los horarios." : "No hay horarios publicados." }}
    </p>

    <template v-else>
      <p class="hl-subtitle">
        <span v-if="bloqueActivo.activo" class="hl-badge">
          <span class="material-symbols-outlined">check_circle</span> Vigente
        </span>
        <span v-else class="hl-badge hl-badge--muted">No vigente</span>
        <span class="hl-subtitle-text">{{ bloqueActivo.titulo }}</span>
      </p>

      <div class="hl-grid">
        <article
          v-for="(item, index) in bloqueActivo.element"
          :key="index"
          class="hl-card"
          :class="{ 'is-featured': index === 0 }"
        >
          <div class="hl-card-head">
            <span class="hl-icon">
              <span class="material-symbols-outlined">{{ obtenerIcono(item.evento, index) }}</span>
            </span>
            <h3 class="hl-card-title">{{ item.evento }}</h3>
          </div>

          <ul class="hl-rows">
            <li v-for="(value, i) in item.values" :key="i" class="hl-row">
              <span class="hl-row-day">{{ value[0] }}</span>
              <span v-if="value[1]" class="hl-row-time">
                <span
                  v-for="(seg, s) in segmentos(value[1])"
                  :key="s"
                  :class="seg.hora ? 'hl-chip' : 'hl-note'"
                >{{ seg.texto }}</span>
              </span>
            </li>
          </ul>
        </article>
      </div>

      <botones-compartir
        class="hl-share"
        etiqueta="Comparte los horarios"
        compacto
        titulo="Horarios de misa · Santo Domingo Cartagena"
        :texto="textoCompartir"
        ruta="/#horarios"
        tipo="horario"
      />
    </template>
  </div>
</template>

<script>
import BotonesCompartir from "@/components/compartir/BotonesCompartir.vue";

const ICONOS = [
  [["misa", "eucarist", "culto"], "church"],
  [["confes", "penitencia", "reconcilia"], "volunteer_activism"],
  [["adoracion", "santisimo", "expuesto"], "auto_awesome"],
  [["bautiz"], "water_drop"],
  [["boda", "matrimonio"], "favorite"],
  [["despacho", "oficina", "secretaria", "atencion"], "desk"],
  [["rosario", "oracion"], "self_improvement"],
  [["catequesis", "formacion"], "menu_book"],
  [["caritas"], "handshake"],
];

function normalizar(texto) {
  return (texto || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export default {
  name: "HorariosList",

  components: { BotonesCompartir },

  props: {
    horarios: {
      type: Array,
      required: true,
    },
    loading: Boolean,
    error: Boolean,
  },

  data() {
    return {
      seleccionado: null,
    };
  },

  computed: {
    // "Santa Misa: Lunes a sábado 19:00h · Domingos 11:30h" (el primer bloque, que es el principal)
    textoCompartir() {
      const item = this.bloqueActivo && this.bloqueActivo.element && this.bloqueActivo.element[0];
      if (!item) return "";
      const filas = (item.values || []).map((v) => v.filter(Boolean).join(" ")).join(" · ");
      return `${item.evento}: ${filas}`.slice(0, 220);
    },
    bloquesOrdenados() {
      // Activos primero
      return [...this.horarios].sort((a, b) => b.activo - a.activo);
    },
    bloqueActivo() {
      return (
        this.bloquesOrdenados.find((b) => b.id === this.seleccionado) ||
        this.bloquesOrdenados[0] ||
        null
      );
    },
  },

  methods: {
    // "11:30h 17:45h (periodo de catequesis)" -> [hora 11:30h] [hora 17:45h] [nota (periodo de catequesis)]
    segmentos(texto) {
      return String(texto)
        .split(/(\d{1,2}[:.]\d{2}\s*h?)/i)
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => ({ texto: t, hora: /^\d{1,2}[:.]\d{2}\s*h?$/i.test(t) }));
    },
    obtenerIcono(evento, index) {
      const texto = normalizar(evento);
      const found = ICONOS.find(([claves]) => claves.some((c) => texto.includes(c)));
      if (found) return found[1];
      return ["church", "auto_awesome", "desk"][index] || "event";
    },
  },
};
</script>

<style scoped>
.hl {
  text-align: left;
  color: var(--sd-text);
}

.hl-share {
  margin-top: 20px;
}

.hl-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.hl-eyebrow {
  margin: 0 0 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--sd-gold);
}

.hl-title {
  margin: 0;
  font-family: var(--sd-font-display);
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700;
  color: var(--sd-text);
}

/* Pestañas */
.hl-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--sd-border);
  border-radius: 12px;
  background: var(--sd-surface-2);
}

.hl-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sd-muted);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.hl-tab:hover {
  color: var(--sd-text);
}

.hl-tab.is-active {
  background: var(--sd-surface);
  color: var(--sd-text);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.hl-tab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a34a;
}

.hl-subtitle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0 0 24px;
}

.hl-subtitle-text {
  color: var(--sd-muted);
}

.hl-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px 3px 6px;
  border-radius: 999px;
  background: rgba(22, 163, 74, 0.12);
  font-size: 0.75rem;
  font-weight: 700;
  color: #15803d;
}

.hl-badge .material-symbols-outlined {
  font-size: 16px;
  color: inherit;
}

.dark .hl-badge {
  color: #4ade80;
}

.hl-badge--muted {
  padding-left: 10px;
  background: var(--sd-surface-2);
  color: var(--sd-muted);
}

.dark .hl-badge--muted {
  color: var(--sd-muted);
}

/* Tarjetas */
.hl-grid {
  display: grid;
  gap: 20px;
}

@media (min-width: 760px) {
  .hl-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hl-card.is-featured,
  .hl-skeleton.is-featured {
    grid-column: 1 / -1;
  }
}

.hl-card {
  padding: 24px 26px;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.hl-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--sd-shadow-lg);
}

.hl-card.is-featured {
  background: linear-gradient(135deg, var(--sd-surface) 0%, var(--sd-primary-soft) 100%);
  border-color: color-mix(in srgb, var(--sd-primary) 25%, var(--sd-border));
}

.hl-card-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.hl-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: var(--sd-primary-soft);
}

.hl-icon .material-symbols-outlined {
  font-size: 24px;
  color: var(--sd-primary);
}

.hl-card.is-featured .hl-icon {
  background: var(--sd-primary);
}

.hl-card.is-featured .hl-icon .material-symbols-outlined {
  color: var(--sd-primary-contrast);
}

.hl-card-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--sd-text);
}

/*
 * Cada fila es una tabla de dos columnas: días | horas.
 * La columna de los días tiene el mismo ancho en todas las filas de la tarjeta,
 * así las horas empiezan siempre en la misma vertical.
 */
.hl-rows {
  --hl-dia: clamp(140px, 32%, 220px);
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.hl-row {
  display: grid;
  grid-template-columns: var(--hl-dia) minmax(0, 1fr);
  align-items: start;
  column-gap: 20px;
  min-width: 0;
  padding: 12px 0;
  border-bottom: 1px solid var(--sd-border);
}

.hl-row:first-child {
  padding-top: 4px;
}

.hl-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.hl-row-day {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  /* Centrado ópticamente con la primera línea de horas (chips de 30px) */
  min-height: 30px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--sd-text);
}

.hl-row-day::before {
  content: "";
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--sd-gold);
}

/* Filas sin hora (p. ej. "Durante la Santa Misa"): el texto ocupa toda la fila */
.hl-row-day:only-child {
  grid-column: 1 / -1;
  font-weight: 400;
  color: var(--sd-muted);
}

.hl-row-day:only-child::before {
  background: var(--sd-border);
}

.hl-row-time {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
}

.hl-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid color-mix(in srgb, var(--sd-primary) 18%, transparent);
  border-radius: 8px;
  background: var(--sd-primary-soft);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--sd-text);
}

.hl-note {
  flex-basis: 100%;
  font-size: 0.82rem;
  font-style: italic;
  line-height: 1.3;
  color: var(--sd-muted);
  overflow-wrap: anywhere;
}

/* Una nota justo tras una hora la acompaña en la misma línea */
.hl-chip + .hl-note {
  flex-basis: auto;
}

/* Móvil: los días encima y las horas debajo */
@media (max-width: 520px) {
  .hl-row {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 8px;
  }

  .hl-row-day {
    min-height: 0;
  }
}

/* Estados */
.hl-empty {
  margin: 0;
  padding: 40px;
  border: 1px dashed var(--sd-border);
  border-radius: var(--sd-radius);
  text-align: center;
  color: var(--sd-muted);
}

.hl-skeleton {
  height: 200px;
  border-radius: var(--sd-radius);
  background: linear-gradient(90deg, var(--sd-surface-2) 25%, var(--sd-surface) 50%, var(--sd-surface-2) 75%);
  background-size: 200% 100%;
  animation: hl-shimmer 1.4s infinite linear;
}

@keyframes hl-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hl-card {
    transition: none;
  }
  .hl-skeleton {
    animation: none;
  }
}
</style>
